import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Menu, Search, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { Sidebar } from "./Sidebar";
import { Logo } from "@/components/Logo";
import { initials } from "@/lib/format";

export function Topbar() {
  const { profile, roles, signOut } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    setIsMac(/Mac|iPhone|iPad|iPod/i.test(navigator.platform));
  }, []);

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  function triggerCmdK() {
    const ev = new KeyboardEvent("keydown", {
      key: "k",
      bubbles: true,
      metaKey: true,
      ctrlKey: true,
    });
    document.dispatchEvent(ev);
  }

  const primaryRole = roles[0] ?? "—";

  return (
    // In standalone su iOS la webview parte sotto la status bar: la barra si
    // allunga di quanto serve invece di finirci sotto.
    <header
      className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-white/10 bg-background/60 px-3 backdrop-blur-md sm:px-4 md:px-6"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        height: "calc(3.5rem + env(safe-area-inset-top))",
      }}
    >
      <div className="flex min-w-0 items-center gap-2 md:hidden">
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={() => setDrawerOpen(true)}
            aria-label="Apri menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <SheetContent side="left" className="w-72 p-0" hideCloseButton>
            <SheetTitle className="sr-only">Navigazione</SheetTitle>
            <Sidebar variant="mobile" onItemClick={() => setDrawerOpen(false)} />
          </SheetContent>
        </Sheet>
        <Logo size={28} />
        {/* Sotto i 380px il marchio esteso ruba spazio ai comandi */}
        <span className="hidden truncate font-display text-sm font-bold tracking-[0.14em] text-gold min-[380px]:inline">
          MACHIAVELLI
        </span>
      </div>

      {/* Quick search bar (apre Command palette) */}
      <button
        onClick={triggerCmdK}
        className="ml-auto mr-2 hidden h-9 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-xs text-muted-foreground transition-colors hover:bg-white/[0.06] md:flex md:min-w-[220px] lg:min-w-[280px]"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="flex-1 text-left">Cerca o premi…</span>
        <kbd className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[10px]">
          {isMac ? "⌘" : "Ctrl"} K
        </kbd>
      </button>

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 md:hidden"
          onClick={triggerCmdK}
          aria-label="Cerca"
        >
          <Search className="h-5 w-5" />
        </Button>

        <div className="hidden text-right text-xs leading-tight md:block">
          <div className="font-medium">{profile?.full_name ?? profile?.email ?? "—"}</div>
          <div className="capitalize text-muted-foreground">{primaryRole.replace("_", " ")}</div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full">
              <Avatar className="h-8 w-8 ring-2 ring-primary/30">
                <AvatarImage src={profile?.avatar_url ?? undefined} alt={profile?.full_name ?? ""} />
                <AvatarFallback className="bg-gradient-brand font-semibold text-primary-foreground">
                  {initials(profile?.full_name ?? profile?.email)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{profile?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
              <UserIcon className="mr-2 h-4 w-4" />
              Profilo & impostazioni
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
